import { type Blueprint } from '@rta/rta-blueprint-library';

import { DisplayMode } from '@r-core/controller/enums/display-mode.enum';
import { BlueprintPageFooter } from '../shared/blueprint-page-footer';
import { BlueprintPageHeader } from '../shared/blueprint-page-header';
import { BlueprintPageWrapper } from '../shared/blueprint-page-wrapper';
import { BlueprintPanelFooter } from '../shared/blueprint-panel-footer';
import { EmbeddedSvgViewport } from '../shared/embedded-svg-viewport';
import { GRANITE_FINISH } from '../helpers/get-countertop-label';
import { getPageNoteScope } from '../helpers/get-page-note-scope';
import { getPageOverrideScope } from '../helpers/get-page-override-scope';
import { PAGE_BODY_PADDING } from '../shared/blueprint-view-styles';
import type { BlueprintPage } from '../types';

export type BlueprintJointCountertopPageProps = {
	page: BlueprintPage;
	blueprint: Blueprint;
	customerName?: string;
	layoutDescription?: string;
	designerName?: string;
	creationDate?: string;
	threeDImageUrl?: string;
};

export const BlueprintJointCountertopPage = (props: BlueprintJointCountertopPageProps): React.ReactElement => {
	const {
		page,
		blueprint,
		customerName,
		layoutDescription,
		designerName,
		creationDate,
		threeDImageUrl
	} = props;

	const kitchen = blueprint.kitchens.getActiveVersion();
	const defaultIsland = kitchen?.design.islands.getDefaultIsland();
	const defaultIslandId = defaultIsland?.id ?? '';

	// Draw ONLY this joint group's islands — not the whole-kitchen overhead.
	const jointIslandIds = page.content?.islands.map((island) => island.id) ?? null;

	// Lead with the granite material label like the single-island countertop page does (via
	// getCountertopNotes) — the joint sheet was missing it (GitHub #306). All joint-group members
	// share one countertop material, so kitchen-level material is authoritative.
	const notes = [
		...(kitchen?.material?.countertop?.isGranite ? [`Granite | ${GRANITE_FINISH}`] : []),
		'Please Note: Countertop seam joins where indicated'
	];

	return (
		<BlueprintPageWrapper pageNumber={page.pageNumber}>
			<BlueprintPageHeader
				title={page.title}
				pageNumber={page.pageNumber}
				customerName={customerName}
				layoutDescription={layoutDescription}
				designerName={designerName}
				creationDate={creationDate}
				threeDImageUrl={threeDImageUrl}
				showThumbnail={true}
			/>

			<div style={{
				flex: 1,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: PAGE_BODY_PADDING,
				overflow: 'hidden'
			}}>
				<EmbeddedSvgViewport
					blueprint={blueprint}
					islandId={defaultIslandId}
					panelId={null}
					displayMode={DisplayMode.InlineManufacturing}
					pageIndex={page.pageNumber}
					pageType={page.pageType}
					overheadIslandIds={jointIslandIds}
				/>
			</div>

			{/* Combined panel footer + page footer */}
			<div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
				<BlueprintPanelFooter scope={getPageOverrideScope(page)} notes={notes} noteScope={getPageNoteScope(page)} />
				<BlueprintPageFooter pageNumber={page.pageNumber} />
			</div>
		</BlueprintPageWrapper>
	);
};
